import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';

const FEATURE_LABELS = {
  help_assistant: 'Help assistant',
  student_fill_blank_generation: 'Fill-in-the-blank practice',
  student_quiz_grading: 'Quiz feedback',
  student_reordering_hint: 'Sentence coach',
  student_reordering_review: 'Sentence answer review',
  student_tts: 'Speech playback',
  structured_generation: 'Structured content generation',
  teacher_content_generation: 'Teacher content generation',
  teacher_word_generation: 'Word practice generation',
  teacher_word_list_normalize: 'Tidying an assignment list',
  teacher_word_list_split: 'Reading an assignment list',
};

const featureLabel = (feature) => FEATURE_LABELS[feature] || String(feature || 'Other AI work').replaceAll('_', ' ');
const money = (value, digits = 2) => `CNY ${Number(value || 0).toFixed(digits)}`;
const budgetStatusLabel = (status) => ({
  normal: 'Within allowance',
  soft_warning: 'Needs attention',
  conservation: 'Near monthly limit',
  blocked: 'Monthly limit reached',
}[status] || 'Status unavailable');

const SuperAdminDashboard = () => {
  const [dau, setDau] = useState([]);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approving, setApproving] = useState({}); // id -> bool
  const [error, setError] = useState('');
  const [impersonateName, setImpersonateName] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageMonth, setUsageMonth] = useState(() => new Date().toISOString().slice(0, 7));

  useEffect(() => {
    const t = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${t}` };
    const run = async () => {
      setError('');
      try {
        const [dRes, uRes] = await Promise.all([
          fetch('/api/superadmin/dau', { headers }),
          fetch('/api/superadmin/users', { headers })
        ]);
        const d = dRes.ok ? await dRes.json() : [];
        const u = uRes.ok ? await uRes.json() : [];
        setDau(Array.isArray(d) ? d : []);
        setUsers(Array.isArray(u) ? u : []);
      } catch (e) {
        setError(e?.message || 'Failed to load');
      }
    };
    run();
    // Load pending teachers initially
    loadPending();
  }, []);

  const loadPending = async () => {
    setPendingLoading(true);
    try {
      const t = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${t}` };
      const r = await fetch('/api/superadmin/pending-teachers', { headers });
      const j = await r.json().catch(() => ([]));
      if (!r.ok) throw new Error(j?.message || 'Failed to load pending teachers');
      setPending(Array.isArray(j) ? j : []);
    } catch (e) {
      setError(e?.message || 'Failed to load pending teachers');
      setPending([]);
    } finally {
      setPendingLoading(false);
    }
  };

  const loadUsage = async (month = usageMonth) => {
    setUsageLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/superadmin/ai-usage/summary?month=${encodeURIComponent(month)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.message || 'Failed to load AI usage');
      setUsage(payload);
    } catch (usageError) {
      setError(usageError?.message || 'Failed to load AI usage');
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    loadUsage(usageMonth);
  }, [usageMonth]);

  const approve = async (it) => {
    const id = it.user_id || it._id || it.username;
    setApproving(prev => ({ ...prev, [id]: true }));
    try {
      const t = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` };
      const body = it.user_id ? { user_id: it.user_id } : { username: it.username };
      const r = await fetch('/api/superadmin/approve-teacher', { method: 'POST', headers, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.message || 'Approve failed');
      // Refresh list
      await loadPending();
    } catch (e) {
      alert(e?.message || 'Approve failed');
    } finally {
      setApproving(prev => ({ ...prev, [id]: false }));
    }
  };

  const loginAs = async ({ username, user_id }) => {
    if (!username && !user_id) {
      alert('Provide a username');
      return;
    }
    setImpersonating(true);
    try {
      const t = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` };
      const body = user_id ? { user_id } : { username };
      const r = await fetch('/api/superadmin/impersonate', { method: 'POST', headers, body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.message || 'Failed to impersonate');
      const token = j?.token;
      if (!token) throw new Error('No token returned');
      localStorage.setItem('token', token);
      // Decide where to go based on role in token (fallback to returned role)
      let role = j?.role;
      try { role = jwtDecode(token)?.role || role; } catch {}
      if (role === 'admin') window.location.href = '/admin/dashboard';
      else if (role === 'superadmin') window.location.href = '/superadmin/dashboard';
      else window.location.href = '/student/dashboard';
    } catch (e) {
      alert(e?.message || 'Impersonation failed');
    } finally {
      setImpersonating(false);
    }
  };

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-800">Super Admin Overview</h1>
      {error && <div className="text-red-600">{error}</div>}
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-xl font-semibold mb-3">Direct Login (Impersonate)</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="text"
            value={impersonateName}
            onChange={(e)=>setImpersonateName(e.target.value)}
            placeholder="Enter username"
            className="px-3 py-2 border rounded w-64"
          />
          <button
            disabled={impersonating || !impersonateName}
            onClick={() => loginAs({ username: impersonateName })}
            className={`px-3 py-2 rounded text-white ${impersonating || !impersonateName ? 'bg-gray-300' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {impersonating ? 'Logging in…' : 'Login as'}
          </button>
          <div className="text-xs text-gray-500">Logs in as the specified account and redirects to its dashboard.</div>
        </div>
      </div>
      <section className="bg-white rounded-xl shadow p-6" aria-labelledby="ai-usage-heading">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h2 id="ai-usage-heading" className="text-xl font-semibold">AI Usage Ledger</h2>
            <p className="text-sm text-gray-500">Token costs only. Prompts, answers, images, and audio are not stored.</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="month"
              value={usageMonth}
              onChange={(event) => setUsageMonth(event.target.value)}
              className="border rounded px-3 py-2"
              aria-label="Usage month"
            />
            <button
              type="button"
              onClick={() => loadUsage()}
              disabled={usageLoading}
              className="px-3 py-2 rounded border bg-gray-50 hover:bg-gray-100 disabled:opacity-50"
            >
              {usageLoading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="border-l-4 border-indigo-500 pl-4 py-1">
            <div className="text-sm text-gray-500">Actual AI cost</div>
            <div className="text-2xl font-semibold">{money(usage?.total_cost_cny)}</div>
          </div>
          <div className="border-l-4 border-emerald-500 pl-4 py-1">
            <div className="text-sm text-gray-500">Students with recorded usage</div>
            <div className="text-2xl font-semibold">{usage?.students?.length || 0}</div>
          </div>
          <div className="border-l-4 border-amber-500 pl-4 py-1">
            <div className="text-sm text-gray-500">Active budget accounts</div>
            <div className="text-2xl font-semibold">{usage?.budgets?.length || 0}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          <div className="overflow-x-auto">
            <h3 className="font-semibold mb-3">Cost by student</h3>
            <table className="min-w-full text-left text-sm">
              <thead><tr className="border-b"><th className="py-2 pr-3">Student</th><th className="py-2 pr-3">Requests</th><th className="py-2 pr-3">Allowance status</th><th className="py-2 text-right">Cost</th></tr></thead>
              <tbody>
                {(usage?.students || []).map((student) => {
                  const budget = (usage?.budgets || []).find((item) => item.scope_type === 'student' && item.scope_id === student.student_id);
                  return (
                    <tr key={student.student_id} className="border-b">
                      <td className="py-2 pr-3" data-i18n-skip>{student.username || student.student_id}</td>
                      <td className="py-2 pr-3">{student.requests || 0} <span>requests</span><br/><span className="text-xs text-gray-500">{student.upstream_calls || 0} <span>provider calls</span></span></td>
                      <td className="py-2 pr-3">{budget ? <><span>{money(budget.actual_cost_cny)} / {money(budget.cap_cny)}</span><br/><span className="text-xs text-gray-500">{budgetStatusLabel(budget.status)}</span></> : 'Not set'}</td>
                      <td className="py-2 text-right">{money(student.cost_cny, 4)}</td>
                    </tr>
                  );
                })}
                {!usageLoading && !(usage?.students || []).length && <tr><td colSpan="4" className="py-5 text-gray-500">No recorded student usage this month.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto">
            <h3 className="font-semibold mb-3">Cost by feature and model</h3>
            <table className="min-w-full text-left text-sm">
              <thead><tr className="border-b"><th className="py-2 pr-3">Feature</th><th className="py-2 pr-3">Model</th><th className="py-2 pr-3">Tokens</th><th className="py-2 text-right">Cost</th></tr></thead>
              <tbody>
                {(usage?.breakdown || []).map((row, index) => (
                  <tr key={`${row.feature}-${row.model}-${index}`} className="border-b">
                    <td className="py-2 pr-3">{featureLabel(row.feature)}</td>
                    <td className="py-2 pr-3" data-i18n-skip>{row.model}</td>
                    <td className="py-2 pr-3 text-xs leading-5">
                      <span>{row.input_tokens || 0} <span>input</span></span><br/>
                      <span>{row.cached_input_tokens || 0} <span>cached</span></span><br/>
                      <span>{row.output_tokens || 0} <span>output</span> + {row.reasoning_tokens || 0} <span>thinking</span></span>
                    </td>
                    <td className="py-2 text-right">{money(row.cost_cny, 4)}</td>
                  </tr>
                ))}
                {!usageLoading && !(usage?.breakdown || []).length && <tr><td colSpan="4" className="py-5 text-gray-500">No AI usage recorded this month.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        {!!(usage?.budgets || []).length && (
          <div className="overflow-x-auto mt-8">
            <h3 className="font-semibold mb-3">All monthly allowances</h3>
            <table className="min-w-full text-left text-sm">
              <thead><tr className="border-b"><th className="py-2 pr-3">Account</th><th className="py-2 pr-3">Used</th><th className="py-2 pr-3">Reserved</th><th className="py-2 pr-3">Monthly limit</th><th className="py-2">Status</th></tr></thead>
              <tbody>{usage.budgets.map((budget) => (
                <tr key={`${budget.scope_type}-${budget.scope_id}`} className="border-b">
                  <td className="py-2 pr-3"><span>{budget.scope_type === 'student' ? 'Student account' : 'Institution account'}</span>: <span data-i18n-skip>{budget.scope_id}</span></td>
                  <td className="py-2 pr-3">{money(budget.actual_cost_cny, 4)}</td>
                  <td className="py-2 pr-3">{money(budget.reserved_cost_cny, 4)}</td>
                  <td className="py-2 pr-3">{money(budget.cap_cny)}</td>
                  <td className="py-2">{budgetStatusLabel(budget.status)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>
      <div id="pending-teachers" className="bg-white rounded-xl shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Pending Teacher Approvals</h2>
          <button onClick={loadPending} className="px-3 py-1.5 text-sm bg-gray-100 border rounded hover:bg-gray-200">Refresh</button>
        </div>
        {pendingLoading ? (
          <div className="text-gray-500 text-sm">Loading…</div>
        ) : pending.length === 0 ? (
          <div className="text-gray-500 text-sm">No pending teacher accounts.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="p-2">Username</th>
                  <th className="p-2">Created at</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {pending.map(it => {
                  const key = it.user_id || it.username;
                  const isBusy = !!approving[key];
                  return (
                    <tr key={key} className="border-b">
                      <td data-i18n-skip className="p-2 font-medium">{it.username}</td>
                      <td className="p-2">{it.created_at || '—'}</td>
                      <td className="p-2">{it.approved ? 'Approved' : 'Pending'}</td>
                      <td className="p-2">
                        <button
                          disabled={isBusy}
                          onClick={() => approve(it)}
                          className={`px-3 py-1.5 rounded ${isBusy ? 'bg-gray-300 text-gray-600' : 'bg-green-600 text-white hover:bg-green-700'}`}
                        >
                          {isBusy ? 'Approving…' : 'Approve'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-xl font-semibold mb-4">DAU (last 14 days)</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {dau.map((d) => (
            <div key={d.date} className="p-3 border rounded">
              <div className="text-sm text-gray-500">{d.date}</div>
              <div className="text-lg font-bold">{d.active_users}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Recent User Logins</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2">Username</th>
                <th className="p-2">Role</th>
                <th className="p-2">Tier</th>
                <th className="p-2">Last login time</th>
                <th className="p-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-b">
                  <td data-i18n-skip className="p-2">{u.username}</td>
                  <td className="p-2">{u.role}</td>
                  <td className="p-2">{u.tier || '—'}</td>
                  <td className="p-2">{u.last_login || 'Never'}</td>
                  <td className="p-2">
                    <button
                      onClick={() => loginAs({ username: u.username, user_id: u.id })}
                      className="px-2 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      Login as
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
